/**
 * Phase K — /transfer-guides/{source_cc_slug}/{destination_program_slug}
 *
 * Programmatic pair page. All prereq/GPA/credit claims trace to
 * verification_records source URLs rendered inline in the sources strip.
 * Intro paragraph is LLM-templated and cached server-side.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const COMMON_MISHAPS_BY_PROGRAM = {
  "uf-dietetics": [
    "Taking BSC 1010 (non-lab version) instead of BSC 2010 (major-track).",
    "Missing the CHM 2210/2211 Organic sequence before applying.",
    "Underestimating the science-GPA threshold for Dietetics — Cs pull the pool down.",
    "Overloading electives that don't articulate to UF's tracking sheet.",
    "Delaying the transfer application until Spring — Fall reviewers see more spots.",
  ],
  "uf-business": [
    "Skipping the C-or-better rule: a C+ ≠ retakeable once posted.",
    "Not taking all seven tracking courses by end of semester 4.",
    "Substituting MAC 1105 for MAC 2233/2311 — UF Warrington requires the calculus track.",
    "Ignoring the no-retake-after-C rule and killing the GPA on a repeat.",
    "Waiting until junior year to declare intent — misses UF advising sync.",
  ],
  "uf-prehealth": [
    "Treating pre-health as a major (it's an advising track).",
    "Skipping the second Physics + lab pair — med schools count both.",
    "Taking online-only labs for Biology or Chem — most med schools reject.",
    "Overloading science courses in one term and tanking the science GPA.",
    "Missing biochemistry entirely — most med schools now require it.",
  ],
  "ucf-business": [
    "Assuming DirectConnect = automatic admission to Business (it isn't).",
    "Not meeting the C-or-better rule in ACG 2021 and ACG 2071.",
    "Below-2.0 cumulative GPA even after DirectConnect certifies the AA.",
    "Skipping CGS 2100 assuming it transfers as an elective — it doesn't for Business.",
    "Delaying the Business application until after AA posts — cohorts fill fast.",
  ],
  "ucf-prehealth": [
    "Skipping BCH 4053 biochemistry — often the make-or-break course.",
    "Taking the algebra-based physics track when med schools expect calc-based.",
    "Under-weighting the pre-health science set relative to gen-ed load.",
    "Missing UCF's pre-health advising sheet updates — deadlines shift year-to-year.",
    "Not planning research or clinical hours alongside coursework.",
  ],
};

function buildBreadcrumbJsonLd(cc, prog, origin) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
      { "@type": "ListItem", position: 2, name: "Transfer Guides", item: origin + "/transfer-guides" },
      { "@type": "ListItem", position: 3, name: cc.name, item: origin + `/community-colleges/${cc.slug}` },
      { "@type": "ListItem", position: 4, name: prog.name, item: origin + `/transfer-guides/${cc.slug}/${prog.slug}` },
    ],
  };
}

function buildCourseJsonLd(prog, dest, origin) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: prog.name,
    description: prog.notes || `${prog.name} at ${dest.name}`,
    provider: {
      "@type": "EducationalOrganization",
      name: dest.name,
      sameAs: `${origin}/universities/${dest.slug || ""}`,
    },
  };
}

export default function TransferGuide() {
  const { source_slug, destination_slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    axios.get(`${API}/catalog/transfer-guides/${source_slug}/${destination_slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
  }, [source_slug, destination_slug]);

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-bone">
        <SEO noindex title="Guide not found" canonical={`/transfer-guides/${source_slug}/${destination_slug}`} />
        <TopNav />
        <main className="max-w-3xl mx-auto px-5 py-24">
          <h1 className="font-serif text-4xl text-ivy">Guide not available</h1>
          <p className="mt-4 text-ink-soft">
            Either the community college or the destination program isn&apos;t verified in our catalog yet.
          </p>
          <Link to="/mishap-corrector" className="mt-6 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6" data-testid="pair-not-found-cta">
            Run a diagnostic instead
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-bone">
        <TopNav />
        <div className="max-w-4xl mx-auto px-5 py-24 text-ink-soft">Loading transfer guide…</div>
      </div>
    );
  }

  const cc = data.source_cc;
  const prog = data.destination_program;
  const dest = data.destination_institution;
  const mishaps = COMMON_MISHAPS_BY_PROGRAM[prog.slug] || [];
  const origin = process.env.REACT_APP_PUBLIC_ORIGIN || "https://ivyleagueconsulting.org";
  const canonical = `/transfer-guides/${cc.slug}/${prog.slug}`;
  const metaTitle = `${cc.short_name} to ${dest.name.split(" ")[0]} ${prog.short_name} Transfer`;
  const metaDesc = `Complete transfer guide from ${cc.name} to ${dest.name} ${prog.name}. Prerequisites, GPA thresholds, articulation notes, and a free diagnostic tool. Sourced from official catalogs.`;

  return (
    <div className="min-h-screen bg-bone" data-testid="transfer-guide-page">
      <SEO
        title={metaTitle}
        description={metaDesc}
        canonical={canonical}
        ogType="article"
        jsonLd={[
          buildBreadcrumbJsonLd(cc, prog, origin),
          buildCourseJsonLd(prog, dest, origin),
        ]}
      />
      <TopNav />

      <main className="max-w-4xl mx-auto px-5 py-14">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Transfer Guide</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight leading-[1.05]" data-testid="pair-h1">
          Transfer from {cc.name} to {prog.name}
        </h1>
        <p className="mt-6 text-ink-soft leading-relaxed max-w-3xl" data-testid="pair-intro">{data.intro_paragraph}</p>

        {/* Prereqs */}
        <section className="mt-12" data-testid="pair-prereqs-section">
          <h2 className="font-serif text-2xl text-ivy tracking-tight">What you need</h2>
          <div className="mt-4 border border-stone/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ivy text-bone text-left uppercase tracking-[0.18em] text-[10px]">
                <tr>
                  <th className="px-4 py-3">Course</th>
                  <th className="px-4 py-3">Min grade</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.prereqs.map((p, i) => (
                  <tr key={i} className={i % 2 ? "bg-bone-dim" : "bg-bone"}>
                    <td className="px-4 py-3 font-mono text-ink">{p.course_code}</td>
                    <td className="px-4 py-3 text-ink">{p.min_grade || "C"}</td>
                    <td className="px-4 py-3 text-ink-soft">{p.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* GPA + Credits */}
        <section className="mt-12" data-testid="pair-thresholds-section">
          <h2 className="font-serif text-2xl text-ivy tracking-tight">GPA + credit thresholds</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="border border-brass/60 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Minimum GPA</div>
              <div className="mt-2 font-serif text-3xl text-ink tabular-nums">{data.gpa_min}</div>
            </div>
            <div className="border border-brass/60 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Credits</div>
              <div className="mt-2 font-serif text-3xl text-ink tabular-nums">{data.credit_count}</div>
            </div>
          </div>
        </section>

        {/* Articulation notes / sources */}
        <section className="mt-12" data-testid="pair-sources-section">
          <h2 className="font-serif text-2xl text-ivy tracking-tight">Articulation notes &amp; sources</h2>
          <ul className="mt-4 space-y-3">
            {data.verification_records.map((r) => (
              <li key={r.record_id} className="border-l-2 border-brass pl-4">
                <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                   className="text-ivy hover:text-brass font-medium text-sm underline-offset-2 hover:underline"
                   data-testid={`pair-source-${r.record_id}`}>
                  {r.source_title}
                </a>
                {r.notes && <p className="text-ink-soft text-xs mt-1 leading-relaxed">{r.notes}</p>}
                {r.last_verified_date && (
                  <div className="text-[10px] uppercase tracking-[0.18em] text-stone mt-1">
                    Last verified {r.last_verified_date}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* Common mishaps */}
        {mishaps.length > 0 && (
          <section className="mt-12" data-testid="pair-mishaps-section">
            <h2 className="font-serif text-2xl text-ivy tracking-tight">Common mishaps students make on this path</h2>
            <ol className="mt-4 list-decimal list-inside space-y-2 text-ink-soft text-sm leading-relaxed">
              {mishaps.map((m, i) => <li key={i}>{m}</li>)}
            </ol>
          </section>
        )}

        {/* CTA */}
        <section className="mt-14 border border-ivy bg-ivy/5 p-6" data-testid="pair-cta-section">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Free diagnostic</div>
          <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">Check your own coursework against this pathway</h2>
          <p className="mt-2 text-ink-soft text-sm leading-relaxed max-w-2xl">
            Upload your Valencia transcript (or paste courses manually) and get a mishap-by-mishap read against the {prog.name} tracking sheet.
          </p>
          <Link
            to={`/mishap-corrector?program=${prog.slug}`}
            className="mt-5 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 hover:bg-brass hover:border-brass"
            data-testid="pair-diagnostic-cta"
          >
            Run your free diagnostic →
          </Link>
        </section>

        {/* Related pair pages */}
        {data.related && data.related.length > 0 && (
          <section className="mt-14" data-testid="pair-related-section">
            <h2 className="font-serif text-2xl text-ivy tracking-tight">Related transfer guides</h2>
            <ul className="mt-4 grid sm:grid-cols-3 gap-3">
              {data.related.map((r, i) => (
                <li key={i}>
                  <Link
                    to={`/transfer-guides/${r.source_cc_slug}/${r.destination_program_slug}`}
                    className="block border border-stone/30 p-4 hover:border-ivy transition-colors"
                    data-testid={`pair-related-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Guide</div>
                    <div className="mt-2 font-serif text-lg text-ivy leading-tight">{r.source_cc_name}</div>
                    <div className="text-xs text-ink-soft mt-1">to {r.destination_program_name}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
